/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * One reservation card in the list: plate + vehicle and the status pill, the
 * booker + created line, the customer, and the expected-arrival / order footer.
 */

import { Typography } from '@euler/app/components/typography';
import {
  LD,
  cardShadowStyle,
} from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { useOpenReservationOrder } from '@euler/app/flows/reservations/functions/useOpenReservationOrder';
import { StatusPill, StatusTone } from '@euler/components/StatusPill';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  Reservation,
  ReservationStatus,
  ReservationStatusLabel,
} from '@euler/model/reservation/Reservation';
import { dateFromValue } from '@euler/utils/datetime';
import { formatVehicleBrandModel } from '@euler/utils/vehicle/brandModel';
import format from 'date-fns/format';
import { ReactNode, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const reservationStatusTone = (
  status: ReservationStatus,
): StatusTone => {
  switch (status) {
    case 'pending':
      // Awaiting confirmation — inert until staff acts.
      return 'neutral';
    case 'confirmed':
      // Booked and due.
      return 'info';
    case 'arrived':
      // Customer on-site — the visit is in progress.
      return 'active';
    case 'converted':
      return 'success';
    case 'cancelled':
    case 'no_show':
      return 'danger';
  }
};

export const formatArrival = (
  value: string | Date | undefined | null,
): string => {
  if (!value) return '—';
  try {
    return format(dateFromValue(value), 'MM-dd HH:mm');
  } catch {
    return '—';
  }
};

const Hairline = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        height: 1,
        backgroundColor: LD.hairline,
        marginVertical: rpx(11),
      }}
    />
  );
});

/** The business-type tags as a row of soft brand chips. */
const TagChips = memo(({ tags }: { tags: string }) => {
  const { rpx } = useResponsiveContext();
  const list = tags
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);
  if (!list.length) return null;
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: rpx(6),
        marginTop: rpx(8),
      }}
    >
      {list.map((tag, index) => (
        <View
          key={`${tag}-${index}`}
          style={{
            backgroundColor: LD.brandSoft,
            borderRadius: rpx(6),
            paddingHorizontal: rpx(8),
            paddingVertical: rpx(3),
          }}
        >
          <Typography.Text size={rpx(11)} color={LD.brand} medium>
            {tag}
          </Typography.Text>
        </View>
      ))}
    </View>
  );
});

/** A label/value pair occupying one side of a row (value aligns to `align`). */
const MetaBlock = memo(
  ({
    label,
    value,
    align = 'left',
    accent,
  }: {
    label: string;
    value: ReactNode;
    align?: 'left' | 'right';
    accent?: string;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{ alignItems: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        <Typography.Text size={rpx(12)} color={LD.mist}>
          {label}
        </Typography.Text>
        <Typography.Text
          size={rpx(14)}
          color={accent ?? LD.ink}
          medium
          mt={rpx(3)}
        >
          {value}
        </Typography.Text>
      </View>
    );
  },
);

export const ReservationCell = memo(
  ({
    reservation,
    onPress,
  }: {
    reservation: Reservation;
    onPress: (reservation: Reservation) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const openReservationOrder = useOpenReservationOrder();
    const onCellPress = useCallback(
      () => onPress(reservation),
      [onPress, reservation],
    );
    // Open the linked order (task detail / service record by its state — the
    // card itself opens the reservation; the nested link wins the press).
    const orderNo = reservation.orderNo;
    const onOpenOrder = useCallback(
      () => void openReservationOrder(orderNo),
      [openReservationOrder, orderNo],
    );

    // Brand · model beside the plate (normalized so the brand shows once).
    const vehicleLabel = formatVehicleBrandModel(
      reservation.vehicleBrandName,
      reservation.vehicleModel,
    );

    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={onCellPress}
        style={{
          backgroundColor: LD.surface,
          borderRadius: rpx(14),
          marginHorizontal: rpx(16),
          marginBottom: rpx(12),
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(15),
          ...cardShadowStyle(rpx),
        }}
      >
        {/* Row 1: plate (signature) + vehicle, and the status chip. */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              marginRight: rpx(10),
            }}
          >
            <Typography.Text
              size={rpx(20)}
              color={LD.ink}
              bold
              letterSpacing={rpx(0.5)}
              numberOfLines={1}
            >
              {reservation.licensePlateNo || '未知车牌'}
            </Typography.Text>
            {vehicleLabel ? (
              <Typography.Text
                size={rpx(13)}
                color={LD.steel}
                medium
                numberOfLines={1}
                style={{ flex: 1, marginLeft: rpx(10) }}
              >
                {vehicleLabel}
              </Typography.Text>
            ) : null}
          </View>
          <StatusPill
            label={ReservationStatusLabel[reservation.status]}
            tone={reservationStatusTone(reservation.status)}
          />
        </View>
        {/* Row 2: who booked it + when it was created. */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: rpx(4),
          }}
        >
          <Typography.Text
            size={rpx(12)}
            color="#8A8F98"
            numberOfLines={1}
            style={{ flex: 1, marginRight: rpx(10) }}
          >
            创建人 · {reservation.createdByName || '—'}
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={LD.mist}>
            {formatArrival(reservation.createdAt)}
          </Typography.Text>
        </View>
        {reservation.tags ? <TagChips tags={reservation.tags} /> : null}

        <Hairline />
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography.Text size={rpx(12)} color={LD.mist}>
            客户
          </Typography.Text>
          <Typography.Text size={rpx(14)} color={LD.ink} medium>
            {reservation.customerName || '未填写客户'}
            {reservation.customerPhone ? (
              <Typography.Text color={LD.steel}>
                {` · ${reservation.customerPhone}`}
              </Typography.Text>
            ) : null}
          </Typography.Text>
        </View>

        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: rpx(12),
          }}
        >
          <MetaBlock
            label="预计到店"
            value={formatArrival(reservation.expectedArrivalAt)}
          />
          <MetaBlock
            label="服务顾问"
            value={reservation.serviceAgentName || '—'}
            align="right"
          />
        </View>

        {/* Last line: the booked items summary, with the converted order on
            the right when the reservation has been opened. */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: rpx(10),
          }}
        >
          <View style={{ flex: 1, marginRight: rpx(10) }}>
            <Typography.Text size={rpx(12)} color={LD.mist}>
              预约项目
            </Typography.Text>
            <Typography.Text
              size={rpx(14)}
              color={LD.ink}
              medium
              mt={rpx(3)}
              numberOfLines={2}
            >
              {reservation.expectedItems || '—'}
            </Typography.Text>
          </View>
          {orderNo ? (
            <View style={{ alignItems: 'flex-end' }}>
              <Typography.Text size={rpx(12)} color={LD.mist}>
                关联订单
              </Typography.Text>
              <TouchableOpacity
                activeOpacity={0.6}
                onPress={onOpenOrder}
                hitSlop={{
                  top: rpx(6),
                  bottom: rpx(6),
                  left: rpx(8),
                  right: rpx(8),
                }}
                style={{ marginTop: rpx(3) }}
              >
                <Typography.Text size={rpx(14)} color={LD.brand} medium>
                  查看订单 →
                </Typography.Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  },
);
